// ts-rest contract for the Entity AML Assessment (SEP-186).
//
// An `EAML-####` record per assessment occasion, stacked on an ENTITY
// Suitability Record over time — the structural sibling of the Contact-level
// AML Assessment contract, anchored to the SUR instead of a Contact. The SUR
// Details page needs two things:
//   - the summary its "Entity AML" section shows (band + score of the most
//     recently COMPLETED assessment),
//   - the stacked history rows (Reference | Score | Risk | Completed By |
//     Completed Date | Status).
//
// The field table itself is imported directly from the package
// (`ENTITY_AML_FIELDS`, `ENTITY_AML_HIGH_RISK_BUSINESSES`, `COUNTRIES`); it is
// not an endpoint.
//
// UNLIKE the personal instrument, the SCORE IS STORED, so it travels on the DTO
// as a real field rather than being recomputed at the seam. SEP-186 parks the
// scoring rule at "1 (medium) for now"; keeping the number on the row means a
// record signed off under today's rule keeps the figure it was signed off with.
// Only the band is derived (from that stored score).

import { initContract } from '@ts-rest/core';
import { z } from 'zod';

import { staffActionErrorSchema } from './agent';

const c = initContract();

import { errorBody } from '../schemas/common';

const statusSchema = z.enum(['draft', 'completed']);
const riskBandSchema = z.enum(['low', 'medium', 'high']);

/**
 * The stored field values, keyed by field key. Yes/No questions store `'yes'` /
 * `'no'`; the country field an ISO 3166-1 alpha-2 code; dates `YYYY-MM-DD`; the
 * rest free text. Only non-blank fields are present.
 */
export const entityAmlAnswersSchema = z.record(z.string(), z.string());
export type EntityAmlAnswersDto = z.infer<typeof entityAmlAnswersSchema>;

export const entityAmlAssessmentDtoSchema = z.object({
    id: z.string(),
    /** EAML-####. */
    reference: z.string().nullable(),
    suitabilityRecordId: z.string(),
    /** The parent record's SUR-#### — the history grid's context column. */
    suitabilityRecordReference: z.string().nullable(),
    /** The owning IM (a `users` row id). */
    imUserId: z.string(),
    status: statusSchema,
    answers: entityAmlAnswersSchema,
    /** The conditional cash-generative-business selection; null unless the
     * business-source-of-income question is answered `yes`. */
    highRiskBusiness: z.string().nullable(),
    /** THE STORED SCORE — from `scoreEntityAml`, a placeholder rule today. */
    score: z.number(),
    /** Derived from `score`, using the personal instrument's banding. */
    band: riskBandSchema,
    /** "Low" / "Medium" / "High". */
    bandLabel: z.string(),
    /** Decision questions carrying an answer / decision questions on the form. */
    answered: z.number(),
    answerable: z.number(),
    /** Calendar completion date (`YYYY-MM-DD`) — null while draft. */
    completionDate: z.string().nullable(),
    createdAt: z.string(),
    /** Completion timestamp — null while draft. */
    completedAt: z.string().nullable(),
    /** The "Completed By" staff user id — null while draft. */
    completedByUserId: z.string().nullable(),
    /** Display name for `completedByUserId`, resolved server-side. */
    completedByName: z.string().nullable(),
});
export type EntityAmlAssessmentDto = z.infer<typeof entityAmlAssessmentDtoSchema>;

/**
 * What the record's Entity AML section header shows: the risk band of the most
 * recently COMPLETED assessment. Every field is null when there is none.
 */
export const entityAmlSummarySchema = z.object({
    assessmentId: z.string().nullable(),
    reference: z.string().nullable(),
    band: riskBandSchema.nullable(),
    /** "Low" / "Medium" / "High". */
    bandLabel: z.string().nullable(),
    score: z.number().nullable(),
    completionDate: z.string().nullable(),
});
export type EntityAmlSummary = z.infer<typeof entityAmlSummarySchema>;

/** The summary + every assessment held against the record, newest first. */
export const listEntityAmlAssessmentsResponseSchema = z.object({
    suitabilityRecordId: z.string(),
    suitabilityRecordReference: z.string(),
    /** The entity the record is held by, for the grid's context. */
    entityName: z.string().nullable(),
    summary: entityAmlSummarySchema,
    assessments: z.array(entityAmlAssessmentDtoSchema),
});
export type ListEntityAmlAssessmentsResponse = z.infer<
    typeof listEntityAmlAssessmentsResponseSchema
>;

/**
 * Save body — the form's editable fields. Nothing derived is sent: the score is
 * computed at the seam from `scoreEntityAml`, so a body cannot dictate it.
 */
const saveBodySchema = z.object({
    /** Absent → create a new draft; present → patch that draft. */
    id: z.string().optional(),
    answers: entityAmlAnswersSchema,
    highRiskBusiness: z.string().nullable().optional(),
});

/** Complete body — the "Completed By" decision (defaults to the caller). */
const completeBodySchema = z
    .object({
        completedByUserId: z.string().optional(),
    })
    .optional();

export const entityAmlAssessmentContract = c.router(
    {
        listEntityAmlAssessments: {
            method: 'GET',
            path: '/suitability-records/:recordId/entity-aml-assessments',
            pathParams: z.object({ recordId: z.string() }),
            responses: {
                200: listEntityAmlAssessmentsResponseSchema,
                403: errorBody,
                404: errorBody,
            },
            summary: "An entity Suitability Record's Entity AML summary + history (newest first)",
        },
        saveEntityAmlAssessment: {
            method: 'POST',
            path: '/suitability-records/:recordId/entity-aml-assessments/save',
            pathParams: z.object({ recordId: z.string() }),
            body: saveBodySchema,
            responses: {
                200: z.object({ assessment: entityAmlAssessmentDtoSchema }),
                400: staffActionErrorSchema,
                403: staffActionErrorSchema,
                404: staffActionErrorSchema,
            },
            summary: "Create or patch an entity record's draft Entity AML assessment",
        },
        completeEntityAmlAssessment: {
            method: 'POST',
            path: '/suitability-records/entity-aml-assessments/:id/complete',
            pathParams: z.object({ id: z.string() }),
            body: completeBodySchema,
            responses: {
                200: z.object({ assessment: entityAmlAssessmentDtoSchema }),
                400: staffActionErrorSchema,
                403: staffActionErrorSchema,
                404: staffActionErrorSchema,
            },
            summary: 'Complete an Entity AML assessment (draft → completed)',
        },
    },
    { pathPrefix: '/api', strictStatusCodes: false },
);
